import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../utils/api";
import usePricing from "../hooks/usePricing";
import "./BookLesson.css";

function BookLesson() {
  const location = useLocation();
  const { packages: PACKAGES } = usePricing();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    postcode: "",
    transmission: "",
    testCentre: "",
    message: "",
    agreeTerms: false,
  });

  const [selectedPackage, setSelectedPackage] = useState(() => {
    const params = new URLSearchParams(location.search);
    const pkg = params.get("package");
    return ["trial", "bronze", "silver", "gold"].includes(pkg) ? pkg : "trial";
  });

  const [files, setFiles] = useState({ licenceFront: null, licenceBack: null });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [testCentres, setTestCentres] = useState([]);
  const [loadingCentres, setLoadingCentres] = useState(true);
  const licenceFrontRef = useRef(null);
  const licenceBackRef = useRef(null);
  const centresAbortRef = useRef(null);
  const tcDropRef = useRef(null);

  // Test centre searchable combobox state
  const [tcSearch, setTcSearch] = useState('');
  const [tcDropOpen, setTcDropOpen] = useState(false);


  // Fetch all active test centres
  const fetchTestCentres = () => {
    if (centresAbortRef.current) centresAbortRef.current.abort();
    const controller = new AbortController();
    centresAbortRef.current = controller;

    setLoadingCentres(true);
    setTestCentres([]);
    api
      .get("/test-centres")
      .then((data) => {
        if (controller.signal.aborted) return;
        const names = (data.testCentres || [])
          .map((tc) => (tc.name || '').trim())
          .filter(Boolean);
        setTestCentres([...new Set(names)]);
      })
      .catch(() => { if (!controller.signal.aborted) setTestCentres([]); })
      .finally(() => { if (!controller.signal.aborted) setLoadingCentres(false); });
  };

  useEffect(() => {
    fetchTestCentres();
    return () => { if (centresAbortRef.current) centresAbortRef.current.abort(); };
  }, []);


  const h = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
  };

  const hPhone = (e) => {
    const cleaned = e.target.value.replace(/[a-zA-Z]/g, "");
    setFormData((f) => ({ ...f, phone: cleaned }));
    if (errors.phone) setErrors((er) => ({ ...er, phone: "" }));
  };

  const hf = (field) => (e) => {
    const file = e.target.files[0] || null;
    setFiles((f) => ({ ...f, [field]: file }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: "" }));
  };


  const validate = () => {
    const e = {};
    if (!selectedPackage) e.selectedPackage = "Please select a lesson package";
    if (!formData.fullName.trim()) e.fullName = "Full name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      e.email = "Valid email is required";
    if (
      formData.postcode.trim() &&
      !/^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i.test(formData.postcode.trim())
    ) {
      e.postcode = "Enter a valid UK postcode (e.g. SW1A 1AA)";
    }
    if (!formData.phone.trim()) {
      e.phone = "Phone number is required";
    } else if (/[a-zA-Z]/.test(formData.phone)) {
      e.phone = "Phone number must not contain letters";
    } else {
      const ph = formData.phone.replace(/[\s\-()+.]/g, "");
      if (!/^(\+44\d{10}|0[1-9]\d{8,9})$/.test(ph))
        e.phone = "Enter a valid UK phone number (e.g. 07123 456789)";
    }
    if (!formData.transmission)
      e.transmission = "Please select a transmission type";
    if (!formData.testCentre)
      e.testCentre = "Please select a test centre / area";
    if (!files.licenceFront)
      e.licenceFront = "Provisional licence front is required";
    if (!files.licenceBack)
      e.licenceBack = "Provisional licence back is required";
    if (!formData.agreeTerms)
      e.agreeTerms = "You must agree to the terms and privacy policy";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    const pkg = PACKAGES.find((p) => p.id === selectedPackage);
    if (!pkg) return;
    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("licenceFront", files.licenceFront);
      payload.append("licenceBack", files.licenceBack);
      payload.append("fullName", formData.fullName);
      payload.append("email", formData.email);
      payload.append("phone", formData.phone || "");
      payload.append("testCentre", formData.testCentre || "");
      payload.append("postcode", formData.postcode || "");
      payload.append("transmission", formData.transmission || "");
      payload.append("message", formData.message || "");
      payload.append("packageType", pkg.id);

      const res = await fetch("/api/bookings/submit", {
        method: "POST",
        body: payload,
      });
      const json = await res.json();

      if (!res.ok || !json.success) {
        setErrors({
          selectedPackage: json.error || "Submission failed. Please try again.",
        });
        return;
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (err) {
      console.error("[BookLesson] Submit error:", err);
      setErrors({
        selectedPackage: "Could not connect to server. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success state ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="bl-page">
        <div className="bl-hero">
          <div className="bl-hero-inner">
            <span className="bl-eyebrow">🇬🇧 DVSA-Approved Instructors</span>
            <h1>Booking Request Submitted</h1>
          </div>
        </div>
        <div
          className="bl-layout"
          style={{
            display: "block",
            maxWidth: 680,
            margin: "0 auto",
            padding: "2rem 1rem 4rem",
          }}
        >
          <div
            style={{
              background: "#f0fdf4",
              border: "2px solid #86efac",
              borderRadius: 16,
              padding: "2.5rem 2rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                marginBottom: "1rem",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "#dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#16a34a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <h2
              style={{
                margin: "0 0 0.75rem",
                color: "#16a34a",
                fontSize: "1.7rem",
              }}
            >
              Request Received!
            </h2>
            <p
              style={{
                margin: "0 0 1.25rem",
                color: "#166534",
                fontSize: "1.05rem",
                lineHeight: 1.65,
              }}
            >
              We've received your booking request and are now checking
              instructor availability in your area.
            </p>
            <div
              style={{
                background: "#fff",
                border: "1px solid #bbf7d0",
                borderRadius: 10,
                padding: "1.25rem 1.5rem",
                marginBottom: "1.5rem",
                textAlign: "left",
              }}
            >
              <p
                style={{
                  margin: "0 0 0.5rem",
                  fontWeight: 700,
                  color: "#0f172a",
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                What happens next
              </p>
              <ol
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  color: "#166534",
                  fontSize: "0.95rem",
                  lineHeight: 2,
                }}
              >
                <li>We match you with an available instructor in your area</li>
                <li>
                  You receive an email with a{" "}
                  <strong>secure payment link</strong> (usually within 2–4
                  hours)
                </li>
                <li>
                  You pay to confirm — then get your instructor's full details
                  and contact info
                </li>
              </ol>
            </div>
            <p
              style={{
                margin: "0 0 0.25rem",
                color: "#64748b",
                fontSize: "0.9rem",
              }}
            >
              A confirmation email has been sent to{" "}
              <strong>{formData.email}</strong>.
            </p>
            <p
              style={{
                margin: "0 0 0.5rem",
                color: "#94a3b8",
                fontSize: "0.82rem",
              }}
            >
              📬 Can't find it? Check your <strong>spam or junk folder</strong>.
            </p>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem" }}>
              Questions?{" "}
              <a href="/contact" style={{ color: "#16a34a" }}>
                Contact us
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bl-page">
      {/* ─── Hero ─── */}
      <div className="bl-hero">
        <div className="bl-hero-inner">
          <span className="bl-eyebrow">🇬🇧 DVSA-Approved Instructors</span>
          <h1>Book Your Lesson</h1>
          <p>
            Fill in your details below. We'll confirm an instructor and email
            you a <strong>secure payment link</strong> — no payment needed right
            now.
          </p>
          <div className="bl-trust-bar">
            <span>✓ Fully insured</span>
            <span>✓ DBS-checked instructors</span>
            <span>✓ No payment until instructor confirmed</span>
            <span>✓ Book in minutes</span>
          </div>
        </div>
      </div>

      {/* ─── Form + Side Panel ─── */}
      <div className="bl-layout">
        <aside className="bl-sidebar">
          {(() => {
            const pkg =
              PACKAGES.find((p) => p.id === selectedPackage) || PACKAGES[0];
            return (
              <div className="bl-sidebar-card bl-sidebar-card--price">
                <div className="bl-price-tag">
                  <span className="bl-price-label">{pkg.label}</span>
                  {pkg.price ? (
                    <>
                      <span className="bl-price-amount">£{pkg.price}</span>
                      <span className="bl-price-unit">
                        for {pkg.hours} hours
                      </span>
                    </>
                  ) : (
                    <span className="bl-price-amount bl-price-amount--tbc">
                      TBC
                    </span>
                  )}
                </div>
                <ul className="bl-price-includes">
                  <li>
                    ✓ {pkg.hours} hour{pkg.hours > 1 ? "s" : ""} on-road tuition
                  </li>
                  <li>✓ DVSA-approved instructor</li>
                  <li>✓ Vehicle provided (insured)</li>
                  <li>✓ Honest progress feedback</li>
                </ul>
              </div>
            );
          })()}

          <div className="bl-sidebar-card">
            <h3>Why Fast Track?</h3>
            <ul className="bl-why-list">
              <li>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="6" />
                    <path d="M8 14l-4 7h16l-4-7" />
                    <path d="M9 11l2 2 4-4" />
                  </svg>
                </span>
                <span>Government-approved instructors</span>
              </li>
              <li>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                    <circle cx="12" cy="9" r="2.5" />
                  </svg>
                </span>
                <span>20 test centres covered</span>
              </li>
              <li>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3" />
                    <rect x="9" y="11" width="14" height="10" rx="2" />
                    <circle cx="12" cy="16" r="1" />
                  </svg>
                </span>
                <span>Manual &amp; automatic</span>
              </li>
              <li>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </span>
                <span>First-time pass rate above national average</span>
              </li>
              <li>
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 5.55 5.55l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </span>
                <span>UK-based support team</span>
              </li>
            </ul>
          </div>

          <div className="bl-sidebar-card bl-sidebar-card--secure">
            <span className="bl-secure-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </span>
            <p>
              <strong>No payment needed right now.</strong> We'll email you a
              secure payment link once your instructor is confirmed. Only then
              will you be asked to pay the upfront booking fee.
            </p>
          </div>
        </aside>

        {/* Form */}
        <main className="bl-form-wrap">
          <form onSubmit={handleSubmit} className="bl-form" noValidate>

            {/* Section 1: Choose Package */}
            <div className="bl-section">
              <div className="bl-section-head">
                <span className="bl-section-num">1</span>
                <div>
                  <div className="bl-section-title">Choose Your Package</div>
                  <div className="bl-section-sub">
                    Select the lesson package that suits you best
                  </div>
                </div>
              </div>
              <div className="bl-packages">
                {PACKAGES.map((pkg) => (
                  <label
                    key={pkg.id}
                    className={`bl-package-card${selectedPackage === pkg.id ? " bl-package-card--selected" : ""}${pkg.comingSoon ? " bl-package-card--soon" : ""}`}
                    onClick={() => {
                      if (!pkg.comingSoon) {
                        setSelectedPackage(pkg.id);
                        setErrors((er) => ({ ...er, selectedPackage: "" }));
                      }
                    }}
                  >
                    <input
                      type="radio"
                      name="selectedPackage"
                      value={pkg.id}
                      checked={selectedPackage === pkg.id}
                      onChange={() => { }}
                      disabled={pkg.comingSoon}
                      style={{ display: "none" }}
                    />
                    <div className="bl-pkg-top">
                      <span className="bl-pkg-tag" style={{ background: pkg.tagColour }}>
                        {pkg.tag}
                      </span>
                      {pkg.comingSoon && (
                        <span className="bl-pkg-soon">Coming Soon</span>
                      )}
                    </div>
                    <div className="bl-pkg-name">{pkg.label}</div>
                    <div className="bl-pkg-price">
                      {pkg.price ? (
                        <>
                          <strong>£{pkg.price}</strong>{" "}
                          <em>/ {pkg.hours} hrs</em>
                        </>
                      ) : (
                        <span className="bl-pkg-tbc">
                          {pkg.hours} hrs &mdash; Price TBC
                        </span>
                      )}
                    </div>
                    <p className="bl-pkg-desc">{pkg.description}</p>
                    {pkg.price > 0 && (
                      <div className="bl-pkg-split">
                        <span className="bl-pkg-split-row">
                          <span>Pay Online Now</span>
                          <strong>£{pkg.upfront}</strong>
                        </span>
                        <span className="bl-pkg-split-row">
                          <span>Instructor Fee</span>
                          <strong>{pkg.remaining > 0 ? `£${pkg.remaining}` : '£0'}</strong>
                        </span>
                      </div>
                    )}
                    {selectedPackage === pkg.id && !pkg.comingSoon && (
                      <span className="bl-pkg-check">✓ Selected</span>
                    )}
                  </label>
                ))}
              </div>
              {errors.selectedPackage && (
                <span className="bl-err">{errors.selectedPackage}</span>
              )}
            </div>

            {/* Section 2: Personal Details */}
            <div className="bl-section">
              <div className="bl-section-head">
                <span className="bl-section-num">2</span>
                <div>
                  <div className="bl-section-title">Personal Details</div>
                  <div className="bl-section-sub">Tell us about yourself</div>
                </div>
              </div>
              <div className="bl-grid">
                <div className={`bl-field${errors.fullName ? " bl-field--err" : ""}`}>
                  <label htmlFor="fullName">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={h}
                    placeholder="Jane Smith"
                  />
                  {errors.fullName && (
                    <span className="bl-err">{errors.fullName}</span>
                  )}
                </div>
                <div className={`bl-field${errors.postcode ? " bl-field--err" : ""}`}>
                  <label htmlFor="postcode">
                    Postcode{" "}
                    <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="postcode"
                    name="postcode"
                    value={formData.postcode}
                    onChange={h}
                    placeholder="e.g. SW1A 1AA"
                    maxLength={8}
                    style={{ textTransform: "uppercase" }}
                  />
                  {errors.postcode && (
                    <span className="bl-err">{errors.postcode}</span>
                  )}
                </div>
                <div className={`bl-field${errors.phone ? " bl-field--err" : ""}`}>
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={hPhone}
                    placeholder="07123 456789"
                    inputMode="tel"
                    maxLength={12}
                  />
                  {errors.phone && (
                    <span className="bl-err">{errors.phone}</span>
                  )}
                </div>
                <div className={`bl-field bl-field--full${errors.email ? " bl-field--err" : ""}`}>
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={h}
                    placeholder="jane@example.com"
                  />
                  {errors.email && (
                    <span className="bl-err">{errors.email}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3: Provisional Licence */}
            <div className="bl-section">
              <div className="bl-section-head">
                <span className="bl-section-num">3</span>
                <div>
                  <div className="bl-section-title">Provisional Driving Licence</div>
                  <div className="bl-section-sub">
                    Upload both sides of your DVLA provisional licence
                  </div>
                </div>
              </div>
              <div className="bl-grid">
                <div className={`bl-field${errors.licenceFront ? " bl-field--err" : ""}`}>
                  <label>Front of Licence *</label>
                  <div
                    className={`bl-upload${errors.licenceFront ? " bl-upload--err" : ""}`}
                    onClick={() => licenceFrontRef.current?.click()}
                  >
                    <input
                      ref={licenceFrontRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={hf("licenceFront")}
                      style={{ display: "none" }}
                    />
                    {!files.licenceFront && <span className="bl-upload-icon">📄</span>}
                    {files.licenceFront ? (
                      <span className="bl-upload-chosen">
                        <span className="bl-upload-check">✓</span>
                        <span className="bl-upload-filename">{files.licenceFront.name}</span>
                      </span>
                    ) : (
                      <>
                        <span className="bl-upload-cta">Click to upload</span>
                        <span className="bl-upload-hint">JPG, PNG or PDF · max 10MB</span>
                      </>
                    )}
                  </div>
                  {errors.licenceFront && (
                    <span className="bl-err">{errors.licenceFront}</span>
                  )}
                </div>
                <div className={`bl-field${errors.licenceBack ? " bl-field--err" : ""}`}>
                  <label>Back of Licence *</label>
                  <div
                    className={`bl-upload${errors.licenceBack ? " bl-upload--err" : ""}`}
                    onClick={() => licenceBackRef.current?.click()}
                  >
                    <input
                      ref={licenceBackRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={hf("licenceBack")}
                      style={{ display: "none" }}
                    />
                    {!files.licenceBack && <span className="bl-upload-icon">📄</span>}
                    {files.licenceBack ? (
                      <span className="bl-upload-chosen">
                        <span className="bl-upload-check">✓</span>
                        <span className="bl-upload-filename">{files.licenceBack.name}</span>
                      </span>
                    ) : (
                      <>
                        <span className="bl-upload-cta">Click to upload</span>
                        <span className="bl-upload-hint">JPG, PNG or PDF · max 10MB</span>
                      </>
                    )}
                  </div>
                  {errors.licenceBack && (
                    <span className="bl-err">{errors.licenceBack}</span>
                  )}
                </div>
              </div>
              <p className="bl-field-note">
                ℹ️ Your provisional licence is required to confirm eligibility.
                Documents are securely stored and only shared with your assigned instructor.
              </p>
            </div>

            {/* Section 4: Lesson Preferences */}
            <div 
              className="bl-section" 
              style={{ 
                position: 'relative', 
                zIndex: tcDropOpen ? 9999 : 20, 
                overflow: 'visible',
                paddingBottom: tcDropOpen ? 280 : undefined,
                transition: 'padding-bottom 0.2s ease-in-out'
              }}
            >
              <div className="bl-section-head">
                <span className="bl-section-num">4</span>
                <div>
                  <div className="bl-section-title">Lesson Preferences</div>
                  <div className="bl-section-sub">
                    Choose your transmission and test centre area
                  </div>
                </div>
              </div>

              {/* Transmission */}
              <div className="bl-field" style={{ marginBottom: "1.25rem" }}>
                <label>Transmission *</label>
                <div className="bl-radio-group">
                  {["Manual", "Automatic"].map((t) => (
                    <label
                      key={t}
                      className={`bl-radio${formData.transmission === t ? " bl-radio--active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="transmission"
                        value={t}
                        checked={formData.transmission === t}
                        onChange={h}
                      />
                      <span className="bl-radio-icon">
                        {t === "Manual" ? "" : ""}
                      </span>
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
                {errors.transmission && (
                  <span className="bl-err">{errors.transmission}</span>
                )}
              </div>

              {/* Test Centre — searchable combobox with portal dropdown */}
              <div
                className={`bl-field${errors.testCentre ? " bl-field--err" : ""}`}
                ref={tcDropRef}
              >
                <label htmlFor="tc-search">Test Centre / Area *</label>

                {/* Searchable combobox */}
                <div style={{ position: 'relative' }}>
                  <input
                    id="tc-search"
                    type="text"
                    autoComplete="off"
                    disabled={loadingCentres}
                    placeholder={
                      loadingCentres
                        ? 'Loading areas…'
                        : formData.testCentre
                          ? formData.testCentre
                          : 'Type to search test centres…'
                    }
                    value={tcSearch}
                    onChange={(e) => {
                      setTcSearch(e.target.value);
                      setTcDropOpen(true);
                      if (formData.testCentre) {
                        setFormData((f) => ({ ...f, testCentre: '' }));
                        if (errors.testCentre) setErrors((er) => ({ ...er, testCentre: '' }));
                      }
                    }}
                    onFocus={() => setTcDropOpen(true)}
                    onBlur={() => {
                      // Delay so onMouseDown on an option fires first
                      setTimeout(() => setTcDropOpen(false), 160);
                    }}
                    style={{
                      width: '100%',
                      paddingRight: '2.2rem',
                      boxSizing: 'border-box',
                      background: formData.testCentre ? '#f0f9ff' : undefined,
                      borderColor: formData.testCentre ? '#0A1F4E' : undefined,
                      fontWeight: formData.testCentre ? 700 : undefined,
                    }}
                  />

                  {/* Chevron / clear icon */}
                  <span
                    onClick={() => {
                      if (formData.testCentre) {
                        setFormData((f) => ({ ...f, testCentre: '' }));
                        setTcSearch('');
                        if (errors.testCentre) setErrors((er) => ({ ...er, testCentre: '' }));
                      } else {
                        setTcDropOpen((v) => !v);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      right: '0.6rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      color: '#64748b',
                      fontSize: '1.1rem',
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title={formData.testCentre ? 'Clear selection' : 'Open list'}
                  >
                    {formData.testCentre ? '✕' : '▾'}
                  </span>

                  {/* Inline dropdown — expands parent section and escapes clipping via dynamic zIndex */}
                  {tcDropOpen && !loadingCentres && (() => {
                    const q = tcSearch.toLowerCase().trim();
                    const filtered = q
                      ? testCentres.filter((c) => c.toLowerCase().includes(q))
                      : testCentres;

                    return (
                      <ul
                        style={{
                          position: 'absolute',
                          zIndex: 99999,
                          top: 'calc(100% + 4px)',
                          left: 0,
                          right: 0,
                          background: '#fff',
                          border: '1.5px solid #0A1F4E',
                          borderRadius: 10,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
                          maxHeight: 260,
                          overflowY: 'auto',
                          margin: 0,
                          padding: '4px 0',
                          listStyle: 'none',
                        }}
                      >
                        {filtered.length === 0 ? (
                          <li style={{ padding: '10px 16px', color: '#94a3b8', fontSize: '0.875rem' }}>
                            No matching test centres
                          </li>
                        ) : filtered.map((c) => (
                          <li
                            key={c}
                            onMouseDown={() => {
                              setFormData((f) => ({ ...f, testCentre: c }));
                              setTcSearch('');
                              setTcDropOpen(false);
                              if (errors.testCentre) setErrors((er) => ({ ...er, testCentre: '' }));
                            }}
                            style={{
                              padding: '9px 16px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: formData.testCentre === c ? 700 : 400,
                            color: formData.testCentre === c ? '#0A1F4E' : '#0f172a',
                            background: formData.testCentre === c ? '#eff6ff' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                          onMouseEnter={(e) => {
                            if (formData.testCentre !== c) e.currentTarget.style.background = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if (formData.testCentre !== c) e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {formData.testCentre === c && (
                            <span style={{ color: '#CF142B', fontSize: '0.75rem' }}>✓</span>
                          )}
                          {c}
                        </li>
                      ))}
                    </ul>
                  );
                })()}
                </div>

                {/* Selected value pill */}
                {formData.testCentre && (
                  <div style={{
                    marginTop: 6,
                    fontSize: '0.8rem',
                    color: '#0A1F4E',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                    <span style={{ color: '#CF142B' }}>✓</span>
                    <span><strong>{formData.testCentre}</strong> selected</span>
                  </div>
                )}

                {errors.testCentre && (
                  <span className="bl-err">{errors.testCentre}</span>
                )}
              </div>
            </div>

            {/* Section 5: Additional Notes */}
            <div className="bl-section">
              <div className="bl-section-head">
                <span className="bl-section-num">5</span>
                <div>
                  <div className="bl-section-title">Additional Notes</div>
                  <div className="bl-section-sub">
                    Optional — availability, experience level, etc.
                  </div>
                </div>
              </div>
              <div className="bl-field">
                <label htmlFor="message">Message (optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={h}
                  rows={4}
                  placeholder="E.g. I'm a complete beginner, available weekends…"
                />
              </div>
            </div>

            {/* Terms + Submit */}
            <div className="bl-footer">
              <div className={`bl-checkbox-row${errors.agreeTerms ? " bl-checkbox-row--err" : ""}`}>
                <input
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={h}
                />
                <label htmlFor="agreeTerms">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer">
                    Terms &amp; Conditions
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  . I confirm I hold a valid UK provisional driving licence. *
                </label>
              </div>
              {errors.agreeTerms && (
                <span className="bl-err">{errors.agreeTerms}</span>
              )}

              <button
                type="submit"
                className="bl-submit-btn"
                disabled={submitting}
              >
                {submitting ? "Submitting your request…" : "Submit Booking Request →"}
              </button>
              <p className="bl-payment-note">
                🕒 No payment is taken now. We'll email you a secure payment
                link once your instructor is confirmed — typically within 2–4 hours.
              </p>
            </div>

          </form>
        </main>
      </div>
    </div>
  );
}

export default BookLesson;